/**
 * Assinatura ICP-Brasil de documentos do Klinvia.
 *
 * Dois caminhos, mesma tela:
 *   A1 — o certificado fica no cofre do servidor e a assinatura sai em um clique.
 *   A3 — token/cartão: a chave nunca sai do dispositivo, então a extensão Web PKI
 *        assina o hash que o servidor calculou e devolvemos a assinatura para ser
 *        selada no documento.
 */
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

export type CertificateKind = 'a1_file' | 'a3_token';

export interface ProfessionalCertificate {
  id: string;
  professional_id: string;
  certificate_kind: CertificateKind;
  label: string | null;
  subject_common_name: string | null;
  subject_cpf: string | null;
  issuer_common_name: string | null;
  certificate_serial: string | null;
  valid_from: string | null;
  valid_until: string | null;
  is_icp_brasil: boolean;
  remember_passphrase: boolean;
  last_used_at: string | null;
  signature_count: number;
  created_at: string;
  professional?: { id: string; full_name: string; registration_number?: string; registration_type?: string } | null;
}

export interface SignedDocumentInfo {
  id: string;
  prescription_number?: string;
  report_number?: string;
  is_signed: boolean;
  signed_at: string | null;
  signature_provider: string | null;
  signed_pdf_url: string | null;
  signature_hash: string | null;
  signature_verification_code: string | null;
  signer_name: string | null;
}

function apiError(error: any, fallback: string): Error {
  if (error?.code === 'ECONNABORTED' || /timeout.*exceeded/i.test(String(error?.message || ''))) {
    return new Error(
      'A assinatura demorou mais que o esperado. Aguarde alguns segundos e tente de novo — a primeira assinatura após um deploy pode levar um pouco mais.',
    );
  }
  const out = new Error(error?.response?.data?.error || error?.message || fallback) as Error & {
    sncrCode?: string;
    sncrDetails?: any;
  };
  const code = error?.response?.data?.code;
  if (typeof code === 'string' && code.startsWith('SNCR_')) {
    out.sncrCode = code;
    out.sncrDetails = error?.response?.data?.details;
  }
  return out;
}

/** Assinatura gera PDF + PAdES + upload: bem mais lento que um CRUD comum. */
const SIGNATURE_REQUEST_TIMEOUT_MS = 120_000;

// ============================================
// COFRE DE CERTIFICADOS
// ============================================

export async function listCertificates(professionalId?: string): Promise<ProfessionalCertificate[]> {
  try {
    const response = await axios.get(getApiUrl('/clinic/professional-certificates'), {
      params: professionalId ? { professional_id: professionalId } : undefined,
    });
    return response.data?.data || [];
  } catch (error) {
    throw apiError(error, 'Não foi possível carregar os certificados.');
  }
}

export async function uploadA1Certificate(params: {
  professionalId: string;
  file: File;
  passphrase: string;
  label?: string;
  rememberPassphrase?: boolean;
}): Promise<{ certificate: ProfessionalCertificate; warnings: string[] }> {
  const form = new FormData();
  form.append('certificate', params.file);
  form.append('professional_id', params.professionalId);
  form.append('passphrase', params.passphrase);
  form.append('certificate_kind', 'a1_file');
  if (params.label) form.append('label', params.label);
  form.append('remember_passphrase', String(Boolean(params.rememberPassphrase)));

  try {
    const response = await axios.post(getApiUrl('/clinic/professional-certificates'), form, {
      timeout: SIGNATURE_REQUEST_TIMEOUT_MS,
    });
    return { certificate: response.data?.certificate, warnings: response.data?.warnings || [] };
  } catch (error) {
    throw apiError(error, 'Não foi possível guardar o certificado.');
  }
}

export async function registerA3Certificate(params: {
  professionalId: string;
  label?: string;
  subjectCommonName?: string;
  subjectCpf?: string;
  issuerCommonName?: string;
  certificateSerial?: string;
  validFrom?: string;
  validUntil?: string;
}): Promise<{ certificate: ProfessionalCertificate; warnings: string[] }> {
  try {
    const response = await axios.post(getApiUrl('/clinic/professional-certificates'), {
      certificate_kind: 'a3_token',
      professional_id: params.professionalId,
      label: params.label,
      subject_common_name: params.subjectCommonName,
      subject_cpf: params.subjectCpf,
      issuer_common_name: params.issuerCommonName,
      certificate_serial: params.certificateSerial,
      valid_from: params.validFrom,
      valid_until: params.validUntil,
    });
    return { certificate: response.data?.certificate, warnings: response.data?.warnings || [] };
  } catch (error) {
    throw apiError(error, 'Não foi possível registrar o token.');
  }
}

export async function updateCertificate(
  id: string,
  changes: { label?: string; remember_passphrase?: boolean },
): Promise<ProfessionalCertificate> {
  try {
    const response = await axios.put(getApiUrl(`/clinic/professional-certificates/${id}`), changes);
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível atualizar o certificado.');
  }
}

export async function deleteCertificate(id: string): Promise<void> {
  try {
    await axios.delete(getApiUrl(`/clinic/professional-certificates/${id}`));
  } catch (error) {
    throw apiError(error, 'Não foi possível remover o certificado.');
  }
}

// ============================================
// ASSINATURA A1 (COFRE)
// ============================================

export async function signPrescriptionWithVault(params: {
  prescriptionId: string;
  certificateId: string;
  passphrase?: string;
}): Promise<SignedDocumentInfo> {
  try {
    const response = await axios.post(getApiUrl(`/clinic/prescriptions/${params.prescriptionId}/sign`), {
      certificate_id: params.certificateId,
      passphrase: params.passphrase || undefined,
    }, { timeout: SIGNATURE_REQUEST_TIMEOUT_MS });
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível assinar o receituário.');
  }
}

// ============================================
// ASSINATURA A3 VIA WEB PKI
// ============================================

/** Fallback se o bundle falhar (path oficial no npm — o antigo /dist/*.min.js retorna 404). */
const WEB_PKI_SCRIPT_URL = 'https://cdn.jsdelivr.net/npm/web-pki@2.18.0/lacuna-web-pki.js';

export interface TokenCertificate {
  thumbprint: string;
  subjectName: string;
  issuerName: string;
  validityEnd?: string;
  pkiBrazil?: { cpf?: string; responsavel?: string };
}

export class WebPkiUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WebPkiUnavailableError';
  }
}

let webPkiPromise: Promise<any> | null = null;

async function resolveLacunaWebPkiConstructor(): Promise<new () => any> {
  const fromWindow = (window as any).LacunaWebPKI;
  if (fromWindow) return fromWindow;

  try {
    const mod = await import('web-pki');
    const ctor = (mod as { default?: new () => unknown }).default || mod;
    if (typeof ctor === 'function') {
      (window as any).LacunaWebPKI = ctor;
      return ctor as new () => any;
    }
  } catch {
    /* tenta CDN abaixo */
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = WEB_PKI_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(
        new WebPkiUnavailableError(
          'Não foi possível carregar a biblioteca de assinatura. Atualize a página ou use outro navegador (Chrome recomendado).',
        ),
      );
    document.head.appendChild(script);
  });

  const loaded = (window as any).LacunaWebPKI;
  if (!loaded) {
    throw new WebPkiUnavailableError('Componente de assinatura não pôde ser carregado.');
  }
  return loaded;
}

/**
 * Carrega e inicia a extensão Web PKI. É gratuita e roda no computador do
 * profissional; é a única forma de um site alcançar a chave de um token A3
 * (incluindo A3 em nuvem via VIDaaS/SafeID, quando o app do provedor está ativo).
 */
function loadWebPki(): Promise<any> {
  if (webPkiPromise) return webPkiPromise;

  webPkiPromise = (async () => {
    const LacunaWebPKI = await resolveLacunaWebPkiConstructor();
    return new Promise((resolve, reject) => {
      const pki = new LacunaWebPKI();
      pki.init({
        ready: () => resolve(pki),
        notInstalled: () => {
          reject(
            new WebPkiUnavailableError(
              'Para usar certificado A3, instale o Web PKI neste computador (get.webpkiplugin.com). '
              + 'Certificado em nuvem (VIDaaS, SafeID etc.): mantenha o app do provedor aberto e conectado.',
            ),
          );
        },
        defaultFail: (error: any) => {
          reject(new WebPkiUnavailableError(error?.userMessage || 'Falha ao iniciar o componente de assinatura.'));
        },
      });
    });
  })().catch((error) => {
    webPkiPromise = null;
    throw error;
  });

  return webPkiPromise;
}

/** Certificados instalados na máquina (token, cartão ou repositório do sistema). */
export async function listTokenCertificates(): Promise<TokenCertificate[]> {
  const pki = await loadWebPki();
  const certificates = await new Promise<TokenCertificate[]>((resolve, reject) => {
    pki
      .listCertificates()
      .success((list: TokenCertificate[]) => resolve(list || []))
      .error((message: string) => reject(new Error(message || 'Não foi possível listar os certificados do computador.')));
  });
  return certificates;
}

/**
 * Assina um receituário com certificado do dispositivo. O servidor monta o PDF e
 * o envelope; o token só devolve a assinatura do hash.
 */
export async function signPrescriptionWithToken(params: {
  prescriptionId: string;
  thumbprint: string;
  certificateId?: string | null;
}): Promise<SignedDocumentInfo> {
  const pki = await loadWebPki();

  const certificateBase64 = await new Promise<string>((resolve, reject) => {
    pki
      .readCertificate(params.thumbprint)
      .success((content: string) => resolve(content))
      .error((message: string) => reject(new Error(message || 'Não foi possível ler o certificado do token.')));
  });

  let prepared: { request_id: string; hash_base64: string };
  try {
    const response = await axios.post(
      getApiUrl(`/clinic/prescriptions/${params.prescriptionId}/sign/prepare`),
      { certificate: certificateBase64, certificate_id: params.certificateId || undefined },
      { timeout: SIGNATURE_REQUEST_TIMEOUT_MS },
    );
    prepared = response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível preparar o documento para assinatura.');
  }

  const signature = await new Promise<string>((resolve, reject) => {
    pki
      .signHash({
        thumbprint: params.thumbprint,
        hash: prepared.hash_base64,
        digestAlgorithm: 'SHA-256',
      })
      .success((value: string) => resolve(value))
      .error((message: string) => reject(new Error(message || 'A assinatura foi cancelada no token.')));
  });

  try {
    const response = await axios.post(
      getApiUrl(`/clinic/prescriptions/${params.prescriptionId}/sign/finalize`),
      { request_id: prepared.request_id, signature },
      { timeout: SIGNATURE_REQUEST_TIMEOUT_MS },
    );
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível concluir a assinatura.');
  }
}

// ============================================
// ATESTADO — ASSINATURA
// ============================================

export async function signCertificateWithVault(params: {
  certificateId: string;
  vaultCertificateId: string;
  passphrase?: string;
}): Promise<SignedDocumentInfo> {
  try {
    const response = await axios.post(getApiUrl(`/clinic/certificates/${params.certificateId}/sign`), {
      certificate_id: params.vaultCertificateId,
      passphrase: params.passphrase || undefined,
    }, { timeout: SIGNATURE_REQUEST_TIMEOUT_MS });
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível assinar o atestado.');
  }
}

export async function signCertificateWithToken(params: {
  certificateId: string;
  thumbprint: string;
  vaultCertificateId?: string | null;
}): Promise<SignedDocumentInfo> {
  const pki = await loadWebPki();

  const certificateBase64 = await new Promise<string>((resolve, reject) => {
    pki
      .readCertificate(params.thumbprint)
      .success((content: string) => resolve(content))
      .error((message: string) => reject(new Error(message || 'Não foi possível ler o certificado do token.')));
  });

  let prepared: { request_id: string; hash_base64: string };
  try {
    const response = await axios.post(
      getApiUrl(`/clinic/certificates/${params.certificateId}/sign/prepare`),
      { certificate: certificateBase64, certificate_id: params.vaultCertificateId || undefined },
      { timeout: SIGNATURE_REQUEST_TIMEOUT_MS },
    );
    prepared = response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível preparar o documento para assinatura.');
  }

  const signature = await new Promise<string>((resolve, reject) => {
    pki
      .signHash({
        thumbprint: params.thumbprint,
        hash: prepared.hash_base64,
        digestAlgorithm: 'SHA-256',
      })
      .success((value: string) => resolve(value))
      .error((message: string) => reject(new Error(message || 'A assinatura foi cancelada no token.')));
  });

  try {
    const response = await axios.post(
      getApiUrl(`/clinic/certificates/${params.certificateId}/sign/finalize`),
      { request_id: prepared.request_id, signature },
      { timeout: SIGNATURE_REQUEST_TIMEOUT_MS },
    );
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível concluir a assinatura.');
  }
}

export function signedCertificatePdfUrl(certificateId: string): string {
  return getApiUrl(`/clinic/certificates/${certificateId}/signed-pdf`);
}

export async function openSignedCertificatePdf(
  certificateId: string,
  signedPdfUrl?: string | null,
): Promise<void> {
  if (signedPdfUrl && /^https?:\/\//i.test(signedPdfUrl)) {
    window.open(signedPdfUrl, '_blank', 'noopener');
    return;
  }

  const response = await axios.get(signedCertificatePdfUrl(certificateId), { responseType: 'blob' });
  const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const opened = window.open(blobUrl, '_blank');
  if (!opened) {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `atestado-assinado-${certificateId.slice(0, 8)}.pdf`;
    link.click();
  }
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

// ============================================
// RELATÓRIO MÉDICO — ASSINATURA
// ============================================

export async function signMedicalReportWithVault(params: {
  reportId: string;
  certificateId: string;
  passphrase?: string;
}): Promise<SignedDocumentInfo> {
  try {
    const response = await axios.post(getApiUrl(`/clinic/medical-reports/${params.reportId}/sign`), {
      certificate_id: params.certificateId,
      passphrase: params.passphrase || undefined,
    }, { timeout: SIGNATURE_REQUEST_TIMEOUT_MS });
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível assinar o relatório médico.');
  }
}

export async function signMedicalReportWithToken(params: {
  reportId: string;
  thumbprint: string;
  certificateId?: string | null;
}): Promise<SignedDocumentInfo> {
  const pki = await loadWebPki();

  const certificateBase64 = await new Promise<string>((resolve, reject) => {
    pki
      .readCertificate(params.thumbprint)
      .success((content: string) => resolve(content))
      .error((message: string) => reject(new Error(message || 'Não foi possível ler o certificado do token.')));
  });

  let prepared: { request_id: string; hash_base64: string };
  try {
    const response = await axios.post(
      getApiUrl(`/clinic/medical-reports/${params.reportId}/sign/prepare`),
      { certificate: certificateBase64, certificate_id: params.certificateId || undefined },
      { timeout: SIGNATURE_REQUEST_TIMEOUT_MS },
    );
    prepared = response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível preparar o documento para assinatura.');
  }

  const signature = await new Promise<string>((resolve, reject) => {
    pki
      .signHash({
        thumbprint: params.thumbprint,
        hash: prepared.hash_base64,
        digestAlgorithm: 'SHA-256',
      })
      .success((value: string) => resolve(value))
      .error((message: string) => reject(new Error(message || 'A assinatura foi cancelada no token.')));
  });

  try {
    const response = await axios.post(
      getApiUrl(`/clinic/medical-reports/${params.reportId}/sign/finalize`),
      { request_id: prepared.request_id, signature },
      { timeout: SIGNATURE_REQUEST_TIMEOUT_MS },
    );
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível concluir a assinatura.');
  }
}

export function signedMedicalReportPdfUrl(reportId: string): string {
  return getApiUrl(`/clinic/medical-reports/${reportId}/signed-pdf`);
}

export async function openSignedMedicalReportPdf(
  reportId: string,
  signedPdfUrl?: string | null,
): Promise<void> {
  if (signedPdfUrl && /^https?:\/\//i.test(signedPdfUrl)) {
    window.open(signedPdfUrl, '_blank', 'noopener');
    return;
  }

  const response = await axios.get(signedMedicalReportPdfUrl(reportId), { responseType: 'blob' });
  const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const opened = window.open(blobUrl, '_blank');
  if (!opened) {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `relatorio-medico-assinado-${reportId.slice(0, 8)}.pdf`;
    link.click();
  }
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

// ============================================
// PEDIDO DE EXAME — ASSINATURA
// ============================================

export async function signExamOrderWithVault(params: {
  orderId: string;
  certificateId: string;
  passphrase?: string;
}): Promise<SignedDocumentInfo> {
  try {
    const response = await axios.post(getApiUrl(`/clinic/exam-orders/${params.orderId}/sign`), {
      certificate_id: params.certificateId,
      passphrase: params.passphrase || undefined,
    }, { timeout: SIGNATURE_REQUEST_TIMEOUT_MS });
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível assinar o pedido de exame.');
  }
}

export async function signExamOrderWithToken(params: {
  orderId: string;
  thumbprint: string;
  certificateId?: string | null;
}): Promise<SignedDocumentInfo> {
  const pki = await loadWebPki();

  const certificateBase64 = await new Promise<string>((resolve, reject) => {
    pki
      .readCertificate(params.thumbprint)
      .success((content: string) => resolve(content))
      .error((message: string) => reject(new Error(message || 'Não foi possível ler o certificado do token.')));
  });

  let prepared: { request_id: string; hash_base64: string };
  try {
    const response = await axios.post(
      getApiUrl(`/clinic/exam-orders/${params.orderId}/sign/prepare`),
      { certificate: certificateBase64, certificate_id: params.certificateId || undefined },
      { timeout: SIGNATURE_REQUEST_TIMEOUT_MS },
    );
    prepared = response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível preparar o documento para assinatura.');
  }

  const signature = await new Promise<string>((resolve, reject) => {
    pki
      .signHash({
        thumbprint: params.thumbprint,
        hash: prepared.hash_base64,
        digestAlgorithm: 'SHA-256',
      })
      .success((value: string) => resolve(value))
      .error((message: string) => reject(new Error(message || 'A assinatura foi cancelada no token.')));
  });

  try {
    const response = await axios.post(
      getApiUrl(`/clinic/exam-orders/${params.orderId}/sign/finalize`),
      { request_id: prepared.request_id, signature },
      { timeout: SIGNATURE_REQUEST_TIMEOUT_MS },
    );
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível concluir a assinatura.');
  }
}

export function signedExamOrderPdfUrl(orderId: string): string {
  return getApiUrl(`/clinic/exam-orders/${orderId}/signed-pdf`);
}

export async function openSignedExamOrderPdf(
  orderId: string,
  signedPdfUrl?: string | null,
): Promise<void> {
  if (signedPdfUrl && /^https?:\/\//i.test(signedPdfUrl)) {
    window.open(signedPdfUrl, '_blank', 'noopener');
    return;
  }

  const response = await axios.get(signedExamOrderPdfUrl(orderId), { responseType: 'blob' });
  const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const opened = window.open(blobUrl, '_blank');
  if (!opened) {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `pedido-exames-assinado-${orderId.slice(0, 8)}.pdf`;
    link.click();
  }
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

// ============================================
// ASSINATURA EM NUVEM (BirdID / VIDaaS)
// ============================================

export type CloudSignatureProviderId = 'birdid' | 'vidaas';

export interface CloudSignatureProvider {
  id: CloudSignatureProviderId;
  label: string;
  enabled: boolean;
  /** `otp` pede o código do app; `push` aguarda a aprovação no celular. */
  challenge: 'otp' | 'push';
  instructions: string;
}

/** No VIDaaS a resposta só volta depois que o titular aprova no celular. */
const CLOUD_SIGNATURE_TIMEOUT_MS = 180_000;

export async function listCloudSignatureProviders(): Promise<CloudSignatureProvider[]> {
  try {
    const response = await axios.get(getApiUrl('/clinic/cloud-signature/providers'));
    return response.data?.data || [];
  } catch (error) {
    throw apiError(error, 'Não foi possível consultar os provedores de assinatura em nuvem.');
  }
}

const CLOUD_SIGN_PATH: Record<'prescription' | 'medical_report' | 'exam_order' | 'certificate', string> = {
  prescription: 'prescriptions',
  medical_report: 'medical-reports',
  exam_order: 'exam-orders',
  certificate: 'certificates',
};

export async function signDocumentWithCloudProvider(params: {
  kind: 'prescription' | 'medical_report' | 'exam_order' | 'certificate';
  documentId: string;
  provider: CloudSignatureProviderId;
  cpf: string;
  otp?: string;
}): Promise<SignedDocumentInfo> {
  try {
    const response = await axios.post(
      getApiUrl(`/clinic/${CLOUD_SIGN_PATH[params.kind]}/${params.documentId}/sign/cloud`),
      {
        provider: params.provider,
        cpf: params.cpf.replace(/\D/g, ''),
        otp: params.otp || undefined,
      },
      { timeout: CLOUD_SIGNATURE_TIMEOUT_MS },
    );
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível assinar com o certificado em nuvem.');
  }
}

// ============================================
// RECEITAS EM LOTE (uma autorização para várias)
// ============================================

export interface BatchSignResult {
  signed: Array<{ id: string; data: SignedDocumentInfo }>;
  failed: Array<{ id: string; error: string; code?: string | null }>;
}

/** Cada receita gera PDF + PAdES no servidor; o lote inteiro vai numa requisição só. */
const BATCH_SIGNATURE_TIMEOUT_MS = 600_000;

/** A1 (senha uma vez) ou BirdID/VIDaaS (um código/aprovação para todas). */
export async function signPrescriptionsBatch(
  params:
    | { prescriptionIds: string[]; method: 'vault'; certificateId: string; passphrase?: string }
    | { prescriptionIds: string[]; method: 'cloud'; provider: CloudSignatureProviderId; cpf: string; otp?: string },
): Promise<BatchSignResult> {
  const body = params.method === 'vault'
    ? {
        method: 'vault',
        prescription_ids: params.prescriptionIds,
        certificate_id: params.certificateId,
        passphrase: params.passphrase || undefined,
      }
    : {
        method: 'cloud',
        prescription_ids: params.prescriptionIds,
        provider: params.provider,
        cpf: params.cpf.replace(/\D/g, ''),
        otp: params.otp || undefined,
      };
  try {
    const response = await axios.post(getApiUrl('/clinic/prescriptions/sign-batch'), body, {
      timeout: BATCH_SIGNATURE_TIMEOUT_MS,
    });
    return response.data?.data || { signed: [], failed: [] };
  } catch (error) {
    throw apiError(error, 'Não foi possível assinar as receitas.');
  }
}

/**
 * A3: o token assina um hash por receita. O certificado é lido uma vez; o PIN
 * costuma ser pedido só na primeira assinatura da sessão do token.
 */
export async function signPrescriptionsBatchWithToken(params: {
  prescriptionIds: string[];
  thumbprint: string;
  onProgress?: (done: number, total: number) => void;
}): Promise<BatchSignResult> {
  const result: BatchSignResult = { signed: [], failed: [] };
  const total = params.prescriptionIds.length;

  for (let i = 0; i < total; i += 1) {
    const id = params.prescriptionIds[i];
    params.onProgress?.(i, total);
    try {
      const data = await signPrescriptionWithToken({ prescriptionId: id, thumbprint: params.thumbprint });
      result.signed.push({ id, data });
    } catch (error: any) {
      result.failed.push({ id, error: error?.message || 'Falha ao assinar.' });
      // Cancelar no token vale para o lote inteiro: não insiste nas próximas.
      if (/cancel/i.test(String(error?.message || ''))) {
        params.prescriptionIds.slice(i + 1).forEach((rest) => {
          result.failed.push({ id: rest, error: 'Assinatura cancelada no token.' });
        });
        break;
      }
    }
  }
  params.onProgress?.(total, total);
  return result;
}

// ============================================
// PDF ASSINADO E CONFERÊNCIA
// ============================================

export type ShareableSignedDocumentKind = 'prescription' | 'medical_report' | 'exam_order' | 'certificate';

/**
 * Link que qualquer pessoa pode abrir no navegador para ver o PDF assinado (sem login).
 * Storage público → URL direta; cofre no servidor → endpoint público com código de conferência.
 */
export function getShareableSignedPdfLink(params: {
  kind: ShareableSignedDocumentKind;
  documentId: string;
  signedPdfUrl?: string | null;
  signatureVerificationCode?: string | null;
}): string | null {
  const external = String(params.signedPdfUrl || '').trim();
  if (/^https?:\/\//i.test(external)) {
    return external;
  }

  const documentId = String(params.documentId || '').trim();
  const code = String(params.signatureVerificationCode || '').trim();
  if (!documentId || !code) return null;

  const encodedCode = encodeURIComponent(code.toUpperCase());
  return getApiUrl(
    `/clinic/signature-validation/${params.kind}/${encodeURIComponent(documentId)}/pdf?codigo=${encodedCode}`,
  );
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  }
}

export function signedPrescriptionPdfUrl(prescriptionId: string): string {
  return getApiUrl(`/clinic/prescriptions/${prescriptionId}/signed-pdf`);
}

/**
 * Abre o PDF assinado. Quando o arquivo está num storage externo, vamos direto na
 * URL: seguir o redirect por XHR levaria o cabeçalho de sessão e quebraria no CORS.
 */
export async function openSignedPrescriptionPdf(
  prescriptionId: string,
  signedPdfUrl?: string | null,
): Promise<void> {
  if (signedPdfUrl && /^https?:\/\//i.test(signedPdfUrl)) {
    window.open(signedPdfUrl, '_blank', 'noopener');
    return;
  }

  const response = await axios.get(signedPrescriptionPdfUrl(prescriptionId), { responseType: 'blob' });
  const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const opened = window.open(blobUrl, '_blank');
  if (!opened) {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `receituario-assinado-${prescriptionId.slice(0, 8)}.pdf`;
    link.click();
  }
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

